import React, { useCallback } from 'react';
import { Dicom } from '@/models/dicom';
import styles from './DicomItem.module.css';

interface IProps {
  dicom: Dicom;
  /** 1-based clip number in the study. */
  displayNumber: number;
  isActive?: boolean;
  isPinned?: boolean;
  /** Clicking can't load this DICOM (every visible pane is pinned). */
  isNavigationBlocked?: boolean;
  onClick?: () => void;
  onHover?: () => void;
  onHoverEnd?: () => void;
  /** The thumbnail image SETTLED — loaded or errored (a broken thumbnail's
   *  fallback is its final state). Feeds the 'thumbnails' readiness gate
   *  (AP-778 §6). Fired at most once per mount. */
  onThumbnailSettled?: () => void;
}

export function DicomItem({
  dicom,
  displayNumber,
  isActive,
  isPinned,
  isNavigationBlocked,
  onClick,
  onHover,
  onHoverEnd,
  onThumbnailSettled,
}: IProps) {
  // A cached image can be complete before React attaches load handlers, so
  // check on ref attach as well as onLoad/onError. settledRef dedupes.
  const settledRef = React.useRef(false);
  const settle = useCallback(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    onThumbnailSettled?.();
  }, [onThumbnailSettled]);
  const imgRef = useCallback(
    (el: HTMLImageElement | null) => {
      if (el?.complete) settle();
    },
    [settle]
  );
  const dragProps = {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      const { dataTransfer } = e;
      dataTransfer.setData('dicom/uuid', dicom.uuid);
      dataTransfer.effectAllowed = 'copy';
    },
  };

  if (!dicom.thumbnail) {
    return (
      <div
        className={styles.thumbnailItem}
        role="button"
        tabIndex={0}
        aria-label={`DICOM ${displayNumber}`}
        data-active={isActive}
        data-pinned={isPinned || undefined}
        aria-disabled={isNavigationBlocked || undefined}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onClick?.();
        }}
        onMouseEnter={onHover}
        onMouseLeave={onHoverEnd}
        {...dragProps}
      >
        <div className={styles.fallbackContent}>
          <div>{displayNumber}</div>
          <div className={styles.fallbackLabel}>No preview</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.thumbnailItem}
      role="button"
      tabIndex={0}
      data-active={isActive}
      data-pinned={isPinned || undefined}
      aria-disabled={isNavigationBlocked || undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick?.();
      }}
      onMouseEnter={onHover}
      onMouseLeave={onHoverEnd}
      title={`DICOM ${displayNumber}${dicom.numberOfFrames ? ` (${dicom.numberOfFrames} frames)` : ''}`}
      {...dragProps}
    >
      <img
        ref={imgRef}
        src={dicom.thumbnail}
        alt={`Dicom thumbnail ${dicom.uuid}`}
        className={styles.thumbnailImage}
        draggable={false}
        onLoad={settle}
        onError={settle}
      />
      <div className={styles.frameCountBadge}>{displayNumber}</div>
    </div>
  );
}
